/* ============================================
   ConversationHeader — Top bar of active chat
   Contact info, status badges, action buttons,
   inline quick-edit for contact name
   ============================================ */
import { useState, useRef, useEffect } from 'react'
import {
  IconArrowLeft, IconWhatsApp, IconMail, IconMessageCircle, IconGlobe,
  IconUser, IconPlus, IconXCircle, IconCheckCircle, IconSearch, IconEdit,
  IconPaperclip, IconBriefcase, IconFileText, IconUsers,
} from '@/components/icons/Icons'
import { showToast } from '@/components/layout/Toast'
import { esGrupoWhatsApp } from '@/lib/utils'
import type { Conversacion, CanalChat, Contacto } from '@/lib/types'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

interface ConversationHeaderProps {
  conversation: Conversacion
  contacto: Contacto | null
  onClose: () => void
  onCreateContact: () => void
  onLinkContact: () => void
  isTyping?: boolean
  onScanTasks?: () => void
  scanningTasks?: boolean
  taskCount?: number
  onContactUpdated?: (contacto: Contacto) => void
  onToggleSearch?: () => void
  onToggleMedia?: () => void
  /** Abre el modal de nota interna. La nota no sale del ERP. */
  onNuevaNota?: () => void
  mediaCount?: number
  /** Abre el modal de vinculación a cliente/proyecto. */
  onVincularProyecto?: () => void
  nombreCliente?: string | null
  nombreProyecto?: string | null
}

function channelIcon(canal: CanalChat, size = 18) {
  switch (canal) {
    case 'whatsapp': return <IconWhatsApp size={size} />
    case 'email': return <IconMail size={size} />
    case 'web_chat': return <IconGlobe size={size} />
    default: return <IconMessageCircle size={size} />
  }
}

function channelLabel(canal: CanalChat): string {
  switch (canal) {
    case 'whatsapp': return 'WhatsApp'
    case 'email': return 'Email'
    case 'web_chat': return 'Web Chat'
    case 'sms': return 'SMS'
    case 'portal': return 'Portal'
    default: return canal
  }
}

/** Show first name + first surname only */
function displayName(contacto: Contacto | null, fallback: string): string {
  if (!contacto) return fallback
  const firstName = (contacto.nombre || '').split(' ')[0] || ''
  const firstSurname = (contacto.apellido || '').split(' ')[0] || ''
  const display = `${firstName} ${firstSurname}`.trim()
  return display || fallback
}

/** Full name for edit fields */
function fullName(contacto: Contacto | null): { nombre: string; apellido: string } {
  return {
    nombre: contacto?.nombre || '',
    apellido: contacto?.apellido || '',
  }
}

export default function ConversationHeader({
  conversation,
  contacto,
  onClose,
  onCreateContact,
  onLinkContact,
  isTyping,
  onScanTasks,
  scanningTasks,
  taskCount,
  onContactUpdated,
  onToggleSearch,
  onToggleMedia,
  onNuevaNota,
  mediaCount,
  onVincularProyecto,
  nombreCliente,
  nombreProyecto,
}: ConversationHeaderProps) {
  const fallbackName = (conversation.metadata as Record<string, unknown>)?.contact_name as string || conversation.chat_id
  const name = displayName(contacto, fallbackName)
  const phone = contacto?.telefono || conversation.chat_id
  const isLinked = conversation.contacto_id !== null
  const esGrupo = conversation.canal === 'whatsapp' && esGrupoWhatsApp(conversation.chat_id)

  // ─── Quick Edit State ───
  const [editing, setEditing] = useState(false)
  const [editNombre, setEditNombre] = useState('')
  const [editApellido, setEditApellido] = useState('')
  const [saving, setSaving] = useState(false)
  const nombreRef = useRef<HTMLInputElement>(null)

  function startEditing() {
    const { nombre, apellido } = fullName(contacto)
    setEditNombre(nombre)
    setEditApellido(apellido)
    setEditing(true)
  }

  useEffect(() => {
    if (editing && nombreRef.current) {
      nombreRef.current.focus()
      nombreRef.current.select()
    }
  }, [editing])

  async function saveQuickEdit() {
    if (!contacto?.id || !editNombre.trim()) return
    setSaving(true)
    try {
      const token = localStorage.getItem('auth_token')
      const res = await fetch(`${API_URL}/api/contactos/${contacto.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          nombre: editNombre.trim(),
          apellido: editApellido.trim() || null,
        }),
      })
      if (res.ok) {
        const updated = await res.json()
        showToast('Contacto actualizado', 'success')
        setEditing(false)
        onContactUpdated?.(updated)
      } else {
        showToast('Error al actualizar', 'error')
      }
    } catch {
      showToast('Error al actualizar', 'error')
    } finally {
      setSaving(false)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') { e.preventDefault(); saveQuickEdit() }
    if (e.key === 'Escape') setEditing(false)
  }

  return (
    <div className="chat-conv-header">
      {/* Back button (mobile) */}
      <button
        className="chat-input__btn"
        onClick={onClose}
        type="button"
        title="Volver"
        style={{ display: 'none' }}
        data-mobile-back
      >
        <IconArrowLeft size={20} />
      </button>

      {/* Avatar / channel */}
      <div className={`chat-conv-item__icon chat-conv-item__icon--${conversation.canal}`}>
        {contacto ? <IconUser size={20} /> : channelIcon(conversation.canal, 20)}
      </div>

      {/* Info */}
      <div className="chat-conv-header__info">
        {editing ? (
          /* ─── Inline Quick Edit ─── */
          <div className="chat-quick-edit">
            <input
              ref={nombreRef}
              className="chat-quick-edit__input"
              value={editNombre}
              onChange={e => setEditNombre(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Nombre"
              disabled={saving}
            />
            <input
              className="chat-quick-edit__input"
              value={editApellido}
              onChange={e => setEditApellido(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Apellido"
              disabled={saving}
            />
            <button
              className="btn btn-sm btn-primary"
              onClick={saveQuickEdit}
              disabled={saving || !editNombre.trim()}
              type="button"
            >
              {saving ? '...' : '✓'}
            </button>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => setEditing(false)}
              disabled={saving}
              type="button"
            >
              ✕
            </button>
          </div>
        ) : (
          /* ─── Normal Display ─── */
          <>
            <div className="chat-conv-header__name">
              <span>{name}</span>
              {contacto && (
                <button
                  className="chat-quick-edit__trigger"
                  onClick={startEditing}
                  type="button"
                  title="Editar nombre"
                >
                  <IconEdit size={12} />
                </button>
              )}
              <span style={{ fontSize: '0.72rem', fontWeight: 400, display: 'inline-flex', alignItems: 'center', gap: 3, color: 'var(--text-muted)' }}>
                {channelIcon(conversation.canal, 12)}
                {channelLabel(conversation.canal)}
              </span>
              {/* Que es un grupo se dice acá UNA vez, con el porqué de que las
                  burbujas no lleven nombre: la API de Wazzup que usamos manda el
                  autor sólo en los mensajes que salen de nuestro número (su
                  `authorName` viaja con `isEcho`), nunca en los que entran. */}
              {esGrupo && (
                <span
                  className="chat-conv-header__badge chat-conv-header__badge--grupo"
                  title="Chat de grupo. La API de Wazzup que usamos no informa qué participante escribió cada mensaje entrante, así que las burbujas del grupo van sin nombre."
                >
                  <IconUsers size={11} /> Grupo
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="chat-conv-header__phone">{phone}</span>
              <span className={`chat-conv-header__badge ${isLinked ? 'chat-conv-header__badge--linked' : 'chat-conv-header__badge--new'}`}>
                {isLinked ? (
                  <><IconCheckCircle size={11} /> Vinculado</>
                ) : (
                  <><IconXCircle size={11} /> No vinculado</>
                )}
              </span>
              {/* Proyecto vinculado: en un grupo del cliente es el dato que importa —
                  dice a qué trabajo pertenece lo que se pide en el hilo. */}
              {onVincularProyecto && (
                <button
                  className="chat-conv-header__proyecto"
                  onClick={onVincularProyecto}
                  type="button"
                  title={nombreProyecto ? 'Cambiar el proyecto vinculado' : 'Vincular este chat a un proyecto'}
                >
                  <IconBriefcase size={11} />
                  {nombreProyecto || nombreCliente || 'Sin proyecto'}
                </button>
              )}
            </div>
          </>
        )}
        {isTyping && (
          <div className="chat-typing">
            <span className="chat-typing__dot" />
            <span className="chat-typing__dot" />
            <span className="chat-typing__dot" />
            <span className="chat-typing__label">Escribiendo...</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="chat-conv-header__actions">
        {/* Search button */}
        {onNuevaNota && (
          <button
            className="chat-conv-header__icon-btn"
            onClick={onNuevaNota}
            type="button"
            title="Agregar nota interna"
          >
            <IconFileText size={18} />
          </button>
        )}

        {onToggleSearch && (
          <button
            className="chat-conv-header__icon-btn"
            onClick={onToggleSearch}
            type="button"
            title="Buscar mensajes"
          >
            <IconSearch size={18} />
          </button>
        )}
        {/* Media/files button */}
        {onToggleMedia && (
          <button
            className="chat-conv-header__icon-btn"
            onClick={onToggleMedia}
            type="button"
            title="Archivos y multimedia"
            style={{ position: 'relative' }}
          >
            <IconPaperclip size={18} />
            {typeof mediaCount === 'number' && mediaCount > 0 && (
              <span className="chat-task-badge">{mediaCount}</span>
            )}
          </button>
        )}
        {/* Scan tasks button */}
        {onScanTasks && (
          <button
            className="btn btn-sm btn-secondary"
            onClick={onScanTasks}
            type="button"
            disabled={scanningTasks}
            title="Identificar tareas con IA"
            style={{ display: 'flex', alignItems: 'center', gap: 4, position: 'relative' }}
          >
            {scanningTasks ? (
              <div className="auth-spinner" style={{ width: 14, height: 14 }} />
            ) : (
              <IconSearch size={14} />
            )}
            {scanningTasks ? 'Analizando...' : 'Tareas'}
            {typeof taskCount === 'number' && taskCount > 0 && (
              <span className="chat-task-badge">{taskCount}</span>
            )}
          </button>
        )}
        {!isLinked && (
          <>
            <button
              className="btn btn-sm btn-primary"
              onClick={onCreateContact}
              type="button"
            >
              <IconPlus size={14} /> Crear Contacto
            </button>
            <button
              className="btn btn-sm btn-secondary"
              onClick={onLinkContact}
              type="button"
            >
              Vincular
            </button>
          </>
        )}
      </div>
    </div>
  )
}
